import React, {useState, useEffect} from 'react';

import styled, {css} from 'styled-components';
import {colors} from './../../styles';

const Dropdown = ({
    label,
    options,
    children,
    onActive,
    onChange,
    value,
    dark,
    borderRadius,
    width,
    top,
    hideCaret,
    ...props
}) => {
    const panelRef = React.useRef();

    const [active, setActive] = useState(false);
    const [currentItem, setCurrentItem] = useState(value || undefined);

    useEffect(() => {
        active && onActive && onActive();

        // eslint-disable-next-line
    }, [active]);

    useEffect(() => {
        onChange && onChange(currentItem);

        // eslint-disable-next-line
    }, [currentItem]);

    useEffect(() => {
        calculatePanelRight();

        window.addEventListener('resize', calculatePanelRight);

        return () => {
            window.removeEventListener('resize', calculatePanelRight);
        };

        // eslint-disable-next-line
    }, [panelRef.current, active]);

    const calculatePanelRight = () => {
        if (panelRef.current && active) {
            if (
                panelRef.current.getBoundingClientRect().right + 24 >
                window.innerWidth
            ) {
                let difference =
                    panelRef.current.getBoundingClientRect().right -
                    window.innerWidth;
                panelRef.current.style.left = `calc(50% - ${difference}px
                    - 1.5rem - 25px)`;
                panelRef.current.children[0].style.left = `calc(50% +
                    ${difference}px + 1.5rem + 25px)`;
            }
        } else {
            if (
                panelRef.current.getBoundingClientRect().right + 24 <
                window.innerWidth
            ) {
                panelRef.current.style.left = `50%`;
                panelRef.current.children[0].style.left = `50%`;
            }
        }
    };

    const handleSelectItem = item => {
        setCurrentItem(item);
        setActive(false);
    };

    return (
        <Container>
            <Label onClick={() => setActive(!active)}>
                {value} {!hideCaret && <CaretDown active={active} />}
            </Label>

            {active && <Backdrop onClick={() => setActive(false)} />}

            <DropdownPanel
                active={active}
                ref={panelRef}
                dark={dark}
                borderRadius={borderRadius}
                width={width}
                top={top}
            >
                <ArrowUp />

                <OptionsContainer>
                    {options &&
                        options.map((item, index) => {
                            return item.label && item.value !== undefined ? (
                                <Option
                                    key={`key${index + 1}`}
                                    onClick={() => handleSelectItem(item.value)}
                                    value={item.value}
                                >
                                    {item.label}
                                </Option>
                            ) : (
                                item && (
                                    <Option
                                        key={`key${index + 1}`}
                                        onClick={() => handleSelectItem(item)}
                                        value={item}
                                    >
                                        {item}
                                    </Option>
                                )
                            );
                        })}
                </OptionsContainer>
            </DropdownPanel>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;

const Label = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    color: #fff;
    font-size: 14px;
    line-height: 18px;
    text-transform: uppercase;
    text-decoration: none;
    font-weight: bold;
    border: 0;
    padding: 0;
    cursor: pointer;
`;

const OptionsContainer = styled.ul`
    overflow-y: auto;
    width: 100%;
    min-width: 212px;
    max-height: 295px;
    background-color: #fff;
    list-style: none;
    z-index: 99;
    min-height: 30px;
    border-radius: 10px;
    margin: 0;
    padding: 0;
    box-sizing: border-box;

    &::-webkit-scrollbar {
        width: 8px;
        padding: 4px 0px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.25);
        border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.35);
    }
`;

const Option = styled.li`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    text-transform: capitalize;
    color: ${colors.cartoonBlack};
    padding: 13px 16px;
    font-size: 18px;
    line-height: 30px;
    font-weight: bold;
    text-align: left;
    cursor: pointer;

    * {
        text-decoration: none;
    }

    &:not(:last-child) {
        border-bottom: 1px solid #efedef;
    }
`;

const ArrowUp = styled.div`
    position: absolute;
    top: -11px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-bottom: 12px solid #fff;
    @media (min-width: 1312px) and (max-width: 1418px) {
        transform: translateX(-72%);
    }
`;

const DropdownPanel = styled.div`
    position: absolute;
    width: 100%;
    min-width: ${({width}) => (width ? width : '212px')};
    background-color: transparent;

    left: 50%;
    transform: translateX(-50%);
    z-index: 99;
    min-height: 30px;
    border-radius: ${({borderRadius}) =>
        borderRadius ? borderRadius : '10px'};
    margin: 0;
    padding: 0;
    margin-top: ${({top}) => (top ? top : '16px')};

    ${({dark}) =>
        dark &&
        css`
            ${ArrowUp} {
                border-bottom-color: #211921;
            }

            ${OptionsContainer} {
                min-width: ${({width}) => (width ? width : '212px')};
                border-radius: ${({borderRadius}) =>
                    borderRadius ? borderRadius : '10px'};

                background-color: #211921;
                box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.65);
                padding: 40px;
            }

            ${Option} {
                padding: 16px 0;

                &:first-child {
                    padding-top: 0px;
                }

                &:last-child {
                    padding-bottom: 0px;
                }

                &:not(:last-child) {
                    border-bottom: 1px solid #3a333a;
                }
            }
        `}

    ${({active}) =>
        !active &&
        `
        display: none !important;
    `}
`;

const CaretDown = styled.i`
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 7px solid #fff;
    display: inline-block;
    margin-left: 9px;

    ${({active}) =>
        active &&
        css`
            transform: scaleY(-1);
        `}
`;

const Backdrop = styled.div`
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 10;
    background-color: transparent;
`;

export default Dropdown;
